import { useAuth } from '@/hooks/useAuth';
import { useEffect, Profiler, ProfilerOnRenderCallback, ReactNode, lazy, Suspense } from 'react';
import Navigation from './Navigation';
import ConvaiErrorBoundary from '@/components/common/ConvaiErrorBoundary';
import { Analytics } from '@vercel/analytics/react';

const ConvaiWidget = lazy(() => import('@/components/common/ConvaiWidget'));

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, profile, isLoading } = useAuth();

  useEffect(() => {
    document.title = profile ? `Nestor - ${profile.role}` : (user ? 'Nestor - Dashboard' : 'Nestor');
  }, [user, profile]);

  const onRenderCallback: ProfilerOnRenderCallback = (
    id,
    phase,
    actualDuration,
    baseDuration,
    startTime,
    commitTime
  ) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Profiler] ${id}:`, {
        phase,
        actualDuration: Math.round(actualDuration),
        baseDuration: Math.round(baseDuration),
        startTime: Math.round(startTime),
        commitTime: Math.round(commitTime),
      });
    }
  };

  if (isLoading) {
    return (
      <Profiler id="Layout-Loading" onRender={onRenderCallback}>
        <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-dark-bg hide-scrollbar">
          <Navigation />
          <main className="main-content flex items-center justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
          </main>
          <ConvaiErrorBoundary>
            <Suspense fallback={null}>
              <ConvaiWidget />
            </Suspense>
          </ConvaiErrorBoundary>
        </div>
      </Profiler>
    );
  }

  return (
    <Profiler id="Layout-Content" onRender={onRenderCallback}>
      <div className="flex min-h-screen flex-col bg-gray-50 dark:bg-dark-bg font-sans hide-scrollbar">
        <Navigation />
        <main className="flex-1">
          <div className="w-full max-w-7xl mx-auto pb-10">
            {children}
          </div>
        </main>
        <ConvaiErrorBoundary>
          <Suspense fallback={null}>
            <ConvaiWidget />
          </Suspense>
        </ConvaiErrorBoundary>
        <Analytics />
      </div>
    </Profiler>
  );
}